import { track, TrackingEventName, type EventPropsOf } from '@heidi/analytics/events';
import { ButtonAction } from '@heidi/analytics/vocabulary';
import type {
  EvidenceAnswerEvent,
  EvidenceSourceClickedEventProps,
} from '../../contracts';

export type EvidenceAnswerBaseProps = Pick<
  EventPropsOf<TrackingEventName.EvidenceReferenceScrollClicked>,
  'evidence_location' | 'region' | 'is_evidence_pro' | 'is_team_member' | 'is_managed'
>;

export function trackEvidenceAnswerEvent(
  event: EvidenceAnswerEvent,
  baseProps: EvidenceAnswerBaseProps
): void {
  switch (event.type) {
    case 'source_selected':
      // Generic object_action event, kept in lockstep with
      // `evidence_source_clicked` for the button-clicked dashboard.
      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          action: ButtonAction.EvidenceSourceSelected,
          label: 'evidence_source',
          feature: 'evidence',
          source_url: event.source_url,
          source_domain: event.source_domain,
          source_title: event.source_title,
        },
      });
      return;
    case 'reference_scroll_clicked':
      track({
        event_name: TrackingEventName.EvidenceReferenceScrollClicked,
        props: {
          ...baseProps,
          reference_index: event.reference_index,
          source_url: event.source_url,
          source_domain: event.source_domain,
          scroll_succeeded: event.scroll_succeeded,
          chat_id: event.chat_id,
          message_id: event.message_id,
          source_type: event.source_type,
          source_title: event.source_title,
          collections: event.collections,
        },
        options: { amplitude: false },
      });
      return;
    case 'references_section_toggled':
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.EvidenceReferencesSectionToggled,
        props: {
          ...baseProps,
          is_expanded: event.is_expanded,
          references_count: event.references_count,
          trigger: event.trigger,
          chat_id: event.chat_id,
          message_id: event.message_id,
        },
      });
      return;
    case 'reference_feedback_submitted':
      track({
        event_name: TrackingEventName.EvidenceReferenceFeedbackSubmitted,
        props: {
          ...baseProps,
          feedback_type: event.feedback_type,
          source_url: event.source_url,
          source_domain: event.source_domain,
          source_title: event.source_title,
          reference_index: event.reference_index,
          chat_id: event.chat_id,
          session_id: event.session_id,
          message_id: event.message_id,
          has_comment: event.has_comment,
          user_comment: event.user_comment,
        },
        options: { amplitude: false },
      });
      return;
    case 'references_panel_toggled':
      track({
        event_name: event.is_expanded
          ? TrackingEventName.PanelOpened
          : TrackingEventName.PanelClosed,
        props: {
          ...baseProps,
          panel_type: event.panel_type,
          trigger: event.trigger,
          feature: event.feature,
          references_count: event.references_count,
          chat_id: event.chat_id,
        },
      });
  }
}

export function trackEvidenceSourceClick(props: EvidenceSourceClickedEventProps): void {
  track({
    event_name: TrackingEventName.EvidenceSourceClicked,
    props,
    options: { amplitude: false },
  });
}
